import React, { useState, useEffect, useRef, useCallback } from "react";
import "./QualityTab.css";
import { useFinancialPlanState } from "../FinancialPlanContext";

const QualityTab = ({
  projectId,
  onAddSpecialist,
  AddUserIcon,
  isEditable = true,
}) => {
  const {
    financialPlanData,
    updateFinancialPlanSection,
    selectedSpecialists,
    removeSpecialist,
    performCalculation,
    isLoading,
    updateSpecialistEfforts,
    getSpecialistEfforts,
  } = useFinancialPlanState();

  const qualitySectionName = "Контроль качества";
  const qualityData = financialPlanData.sections.quality_control;
  const [bonusPercent, setBonusPercent] = useState(qualityData.bonus_percent);
  const [qualityPercent, setQualityPercent] = useState(
    qualityData.quality_percent || 5
  );
  const [editingFields, setEditingFields] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const prevSpecialistsRef = useRef({});

  // Синхронизируем проценты с данными из контекста при их изменении
  useEffect(() => {
    if (qualityData.bonus_percent !== undefined) {
      setBonusPercent(qualityData.bonus_percent);
    }
    if (qualityData.quality_percent !== undefined) {
      setQualityPercent(qualityData.quality_percent);
    }
  }, [qualityData.bonus_percent, qualityData.quality_percent]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ru-RU").format(value);
  };

  // Функция для запуска калькуляции контроля качества
  // Теперь payload формируется автоматически в контексте из selectedSpecialists
  const triggerQualityCalculation = useCallback(async () => {
    try {
      setIsCalculating(true);
      // Запускаем калькуляцию без переопределений - контекст автоматически соберет данные из selectedSpecialists
      await performCalculation(projectId, {});
    } catch (error) {
      console.error("Ошибка калькуляции при изменении специалистов:", error);
    } finally {
      setIsCalculating(false);
    }
  }, [performCalculation, projectId]);

  // Отслеживаем изменения специалистов и запускаем калькуляцию
  useEffect(() => {
    const prevSpecialists = prevSpecialistsRef.current;
    const currentSpecialists = selectedSpecialists || {};

    // Проверяем, изменились ли специалисты для контроля качества
    const qualitySectionName = "Контроль качества";
    const qualitySpecialistKeys = Object.keys(currentSpecialists).filter(
      (key) => key.startsWith(`${qualitySectionName}-`)
    );

    let hasChanges = false;
    for (const key of qualitySpecialistKeys) {
      const prevValue = prevSpecialists[key];
      const currentValue = currentSpecialists[key];

      // Сравниваем массивы специалистов
      if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
        hasChanges = true;
        break;
      }
    }

    // Если есть изменения и это не первая загрузка
    if (hasChanges && Object.keys(prevSpecialists).length > 0) {
      console.log(
        "Обнаружены изменения в специалистах контроля качества, запускаем калькуляцию"
      );
      triggerQualityCalculation();
    }

    // Обновляем ссылку на предыдущее состояние
    prevSpecialistsRef.current = { ...currentSpecialists };
  }, [selectedSpecialists, triggerQualityCalculation]);

  // Функции для работы с исполнителями
  const handleSpecialistDelete = (blockName, sectionName, specialistIndex) => {
    removeSpecialist(sectionName, blockName, specialistIndex);
  };

  const getSpecialistKey = (sectionName, roleName) => {
    return `${sectionName}-${roleName}`;
  };

  const toggleRoleExpanded = (roleName) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  };

  // Функции для редактирования полей
  const handleStartEdit = (fieldName, currentValue) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    setTempValues((prev) => ({
      ...prev,
      [fieldName]: currentValue,
    }));
  };

  const handleSaveEdit = async (fieldName, value) => {
    const numValue = Number(value);
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }));

    if (fieldName === "bonus_percent") {
      setBonusPercent(numValue);
      await updateFinancialPlanSection("quality_control", {
        bonus_percent: numValue,
      });

      // Запускаем калькуляцию при изменении процента бонусов
      try {
        setIsCalculating(true);
        await performCalculation(projectId, {
          quality_control: { bonus_percent: numValue },
        });
      } catch (error) {
        console.error(
          "Ошибка калькуляции при изменении процента бонусов:",
          error
        );
      } finally {
        setIsCalculating(false);
      }
    } else if (fieldName === "quality_percent") {
      setQualityPercent(numValue);
      await updateFinancialPlanSection("quality_control", {
        quality_percent: numValue,
      });

      // Запускаем калькуляцию при изменении процента контроля качества
      try {
        setIsCalculating(true);
        await performCalculation(projectId, {
          quality_control: { quality_percent: numValue },
        });
      } catch (error) {
        console.error(
          "Ошибка калькуляции при изменении процента контроля качества:",
          error
        );
      } finally {
        setIsCalculating(false);
      }
    }
  };

  const handleCancelEdit = (fieldName) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }));
  };

  const handleTempValueChange = (fieldName, value) => {
    setTempValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleStartEditNumber = (fieldKey, currentValue) => {
    setEditingFields((prev) => ({ ...prev, [fieldKey]: true }));
    setTempValues((prev) => ({ ...prev, [fieldKey]: currentValue }));
  };

  const handleCancelEditNumber = (fieldKey) => {
    setEditingFields((prev) => ({ ...prev, [fieldKey]: false }));
  };

  const getSpecialistEffort = (roleName, specIdx, role, totalSpecs) => {
    const stored = getSpecialistEfforts(qualitySectionName, roleName, specIdx);
    const n = totalSpecs || 1;
    const defaults = {
      day_efforts: (role?.labor_days ?? 0) / n,
      bonus_day_efforts: (role?.bonus_days ?? 0) / n,
      money_expenditures: Math.round((role?.direct_costs ?? 0) / n),
      bonus_money_expenditures: Math.round((role?.bonus_costs ?? 0) / n),
    };
    if (stored && typeof stored.day_efforts === "number") {
      return { ...defaults, ...stored };
    }
    return defaults;
  };

  const getRoleTotalsFromSpecialists = (roleName, role, specialists) => {
    if (!specialists || specialists.length === 0) {
      return {
        day_efforts: role?.labor_days ?? 0,
        bonus_day_efforts: role?.bonus_days ?? 0,
        money_expenditures: role?.direct_costs ?? 0,
        bonus_money_expenditures: role?.bonus_costs ?? 0,
      };
    }
    return specialists.reduce(
      (acc, _, specIdx) => {
        const e = getSpecialistEffort(roleName, specIdx, role, specialists.length);
        acc.day_efforts += e.day_efforts ?? 0;
        acc.bonus_day_efforts += e.bonus_day_efforts ?? 0;
        acc.money_expenditures += e.money_expenditures ?? 0;
        acc.bonus_money_expenditures += e.bonus_money_expenditures ?? 0;
        return acc;
      },
      { day_efforts: 0, bonus_day_efforts: 0, money_expenditures: 0, bonus_money_expenditures: 0 }
    );
  };

  const handleSaveSpecialistEffort = async (roleName, specIdx, fieldName, value) => {
    const num = parseFloat(value);
    if (fieldName === "day_efforts") {
      updateSpecialistEfforts(qualitySectionName, roleName, specIdx, { day_efforts: isNaN(num) ? 0 : num });
    }
    try {
      setIsCalculating(true);
      await performCalculation(projectId);
    } finally {
      setIsCalculating(false);
    }
  };

  // Функция для обработки изменения чекбокса бонусов
  const handleBonusCheckboxChange = async (checked) => {
    updateFinancialPlanSection("quality_control", {
      include_bonuses: checked,
    });

    // Запускаем калькуляцию при изменении чекбокса
    try {
      setIsCalculating(true);
      await performCalculation(projectId, {
        quality_control: {
          include_bonuses: checked,
        },
      });
    } catch (error) {
      console.error(
        "Ошибка калькуляции при изменении чекбокса бонусов:",
        error
      );
    } finally {
      setIsCalculating(false);
    }
  };

  // Компонент для редактируемого поля
  const EditableField = ({
    fieldName,
    value,
    isEditing,
    tempValue,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onTempValueChange,
    isEditable = true,
  }) => {
    const isLoadingField = isLoading || isCalculating || !isEditable;
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(
      value === undefined || value === null ? "" : String(value)
    );

    // Обновляем локальное значение при изменении props или при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        setLocalValue(
          tempValue === undefined || tempValue === null ? "" : String(tempValue)
        );
      } else {
        setLocalValue(
          value === undefined || value === null ? "" : String(value)
        );
      }
    }, [isEditing, value, tempValue]);

    // Автофокус и выделение текста при входе в режим редактирования
    useEffect(() => {
      if (isEditing && !isLoadingField && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing, isLoadingField]);

    if (isEditing && !isLoadingField) {
      return (
        <input
          type="number"
          step="0.1"
          min="0"
          max="100"
          value={localValue}
          ref={inputRef}
          onChange={(e) => {
            const raw = e.target.value;
            // Разрешаем только цифры и одну точку/запятую, убираем остальные символы
            const sanitized = raw
              .replace(/,/g, ".")
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1");
            setLocalValue(sanitized);
            // Обновляем родительское состояние для синхронизации
            onTempValueChange(fieldName, sanitized);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              onSaveEdit(fieldName, localValue);
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setLocalValue(
                value === undefined || value === null ? "" : String(value)
              );
              onCancelEdit(fieldName);
            }
          }}
          onBlur={() => {
            // Автоматически сохраняем при потере фокуса
            onSaveEdit(fieldName, localValue);
          }}
          disabled={!isEditable}
          style={{
            width: "80px",
            padding: "6px 14px",
            border: "1px solid #007bff",
            borderRadius: "7px",
            fontSize: "17px",
            fontWeight: "600",
            color: "#2256a1",
            background: "#FFFFFF",
            textAlign: "center",
            outline: "none",
          }}
        />
      );
    }

    return (
      <div
        className="quality-input"
        style={{
          cursor: isLoadingField ? "not-allowed" : "pointer",
          border: "1px solid #e0e0e0",
          background: isLoadingField ? "#F4F6FA" : "#f5f8fa",
          opacity: isLoadingField ? 0.6 : 1,
        }}
        onClick={() =>
          isEditable && !isLoadingField && onStartEdit(fieldName, value)
        }
        title={
          isLoadingField
            ? "Выполняется калькуляция..."
            : "Нажмите для редактирования"
        }
      >
        {isLoadingField ? "⏳" : `${value}%`}
      </div>
    );
  };

  // Редактируемое числовое поле для специалистов (только трудозатраты)
  const EditableNumberField = ({
    roleName,
    specIdx,
    fieldName,
    value,
    isEditing,
    tempValue,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onTempValueChange,
    format = (v) => String(v ?? ""),
    isEditable = true,
  }) => {
    const fieldKey = `quality-${roleName}-${specIdx}-${fieldName}`;
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(
      value === undefined || value === null ? "" : String(value)
    );

    useEffect(() => {
      if (isEditing) {
        setLocalValue(tempValue === undefined || tempValue === null ? "" : String(tempValue));
      } else {
        setLocalValue(value === undefined || value === null ? "" : String(value));
      }
    }, [isEditing, value, tempValue]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    if (isEditing) {
      return (
        <input
          type="number"
          step="0.1"
          min="0"
          value={localValue}
          ref={inputRef}
          onChange={(e) => {
            const raw = e.target.value;
            const sanitized = raw
              .replace(/,/g, ".")
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1");
            setLocalValue(sanitized);
            onTempValueChange(fieldKey, sanitized);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSaveEdit(roleName, specIdx, fieldName, localValue);
            } else if (e.key === "Escape") {
              e.preventDefault();
              setLocalValue(value === undefined || value === null ? "" : String(value));
              onCancelEdit(fieldKey);
            }
          }}
          onBlur={() => onSaveEdit(roleName, specIdx, fieldName, localValue)}
          disabled={!isEditable || isCalculating || isLoading}
          style={{
            width: "56px",
            padding: "4px 8px",
            border: "1px solid #007bff",
            borderRadius: "4px",
            fontSize: "14px",
            textAlign: "center",
            outline: "none",
            background: "white",
          }}
        />
      );
    }

    return (
      <span
        style={{
          fontSize: "14px",
          display: "inline-block",
          cursor: isEditable ? "pointer" : "default",
          padding: "6px 10px",
          border: "1.5px dashed #B8C2D1",
          borderRadius: "6px",
          background: isEditable ? "#F9FBFF" : "#F4F6FA",
          color: "#1F2937",
          minWidth: "56px",
          textAlign: "center",
        }}
        onClick={() => isEditable && !isCalculating && !isLoading && onStartEdit(fieldKey, value)}
      >
        {format(value)}
      </span>
    );
  };

  const roles = qualityData.sections || [];

  return (
    <div className="quality-table-block">
      {/* Чекбокс "Учитывать бонусы при расчете" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Учитывать бонусы при расчете:
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={qualityData.include_bonuses}
              onChange={(e) => handleBonusCheckboxChange(e.target.checked)}
              disabled={!isEditable || isCalculating || isLoading}
            />
            <span className="slider"></span>
          </label>
          {isCalculating && (
            <div
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "12px",
                color: "#007bff",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>⏳</span>
              <span>Калькуляция...</span>
            </div>
          )}
        </div>
        {/* <button
          onClick={() => {}}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
          Добавить раздел
        </button> */}
      </div>

      <div className="quality-title-row">
        <div className="quality-title-block">
          <div className="quality-title">% доля контроля качества</div>
          <EditableField
            fieldName="quality_percent"
            value={qualityPercent}
            isEditing={editingFields.quality_percent}
            tempValue={tempValues.quality_percent}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onTempValueChange={handleTempValueChange}
            isEditable={isEditable}
          />
        </div>
        <div className="quality-title-block">
          <div className="quality-title">% доля бонусов от ПЗ</div>
          <EditableField
            fieldName="bonus_percent"
            value={bonusPercent}
            isEditing={editingFields.bonus_percent}
            tempValue={tempValues.bonus_percent}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onTempValueChange={handleTempValueChange}
            isEditable={isEditable}
          />
        </div>
      </div>

      <div className="quality-table-mock">
        <div className="quality-expanded-hint" style={{ marginBottom: "8px", fontSize: "13px", color: "#666" }}>
          💡 Кликните по числу, чтобы отредактировать. Изменения сохраняются при потере фокуса.
        </div>
        {roles.length > 0 ? (
          <table className="quality-roles-table">
            <thead>
              <tr>
                <th className="quality-th-name"></th>
                <th>Трудозатраты (дн)</th>
                <th>Бонусы в днях (дн)</th>
                <th>Прямые затраты (тг)</th>
                <th>Бонусы в деньгах (тг)</th>
                <th className="quality-th-action"></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => {
                const specialistKey = getSpecialistKey(qualitySectionName, role.name);
                const specialists = selectedSpecialists?.[specialistKey] || [];
                const isExpanded = expandedRoles.has(role.name);
                const totals = getRoleTotalsFromSpecialists(role.name, role, specialists);

                return (
                  <React.Fragment key={role.name}>
                    <tr
                      className="quality-role-row"
                      onClick={() => toggleRoleExpanded(role.name)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="quality-td-name">
                        <span
                          className="section-arrow"
                          style={{
                            transform: isExpanded ? "rotate(90deg)" : "none",
                            display: "inline-block",
                            marginRight: "6px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRoleExpanded(role.name);
                          }}
                        >
                          ▶
                        </span>
                        <span>{role.name}</span>
                        {isExpanded && specialists.length > 0 && (
                          <span style={{ color: "#666", fontWeight: "normal" }}>
                            {" "}({specialists.length}{" "}
                            {specialists.length === 1 ? "исполнитель" : specialists.length < 5 ? "исполнителя" : "исполнителей"})
                          </span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>{Number(totals.day_efforts).toFixed(1)}</td>
                      <td onClick={(e) => e.stopPropagation()}>{Number(totals.bonus_day_efforts).toFixed(1)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span className="tenge-inline">₸</span> {formatCurrency(totals.money_expenditures)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <span className="tenge-inline">₸</span> {formatCurrency(totals.bonus_money_expenditures)}
                      </td>
                      <td className="quality-td-action"></td>
                    </tr>
                    {isExpanded && (
                      <>
                        {specialists.map((specialist, specIdx) => {
                          const effort = getSpecialistEffort(role.name, specIdx, role, specialists.length);
                          const specLabel =
                            specialist.type === "employee"
                              ? specialist.fullName || specialist.name || `Специалист ${specIdx + 1}`
                              : specialist.fullName || specialist.name || `Субподрядчик ${specIdx + 1}`;
                          const fieldKeyDay = `quality-${role.name}-${specIdx}-day_efforts`;

                          return (
                            <tr
                              key={specIdx}
                              className="quality-specialist-row"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <td className="quality-td-name">{specLabel}</td>
                              <td>
                                <EditableNumberField
                                  roleName={role.name}
                                  specIdx={specIdx}
                                  fieldName="day_efforts"
                                  value={effort.day_efforts ?? 0}
                                  isEditing={editingFields[fieldKeyDay]}
                                  tempValue={tempValues[fieldKeyDay]}
                                  onStartEdit={handleStartEditNumber}
                                  onSaveEdit={(rName, sIdx, fName, val) => {
                                    setEditingFields((prev) => ({ ...prev, [fieldKeyDay]: false }));
                                    handleSaveSpecialistEffort(rName, sIdx, fName, val);
                                  }}
                                  onCancelEdit={handleCancelEditNumber}
                                  onTempValueChange={handleTempValueChange}
                                  format={(v) => (v ?? 0).toFixed(1)}
                                  isEditable={isEditable}
                                />
                              </td>
                              <td>{(effort.bonus_day_efforts ?? 0).toFixed(1)}</td>
                              <td>
                                <span className="tenge-inline">₸</span> {formatCurrency(effort.money_expenditures ?? 0)}
                              </td>
                              <td>
                                <span className="tenge-inline">₸</span> {formatCurrency(effort.bonus_money_expenditures ?? 0)}
                              </td>
                              <td className="quality-td-action">
                                {isEditable && (
                                  <button
                                    type="button"
                                    className="expanded-specialist-delete-btn"
                                    onClick={() =>
                                      handleSpecialistDelete(role.name, qualitySectionName, specIdx)
                                    }
                                    title="Удалить"
                                  >
                                    🗑
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="quality-add-row expanded-block-add-row" onClick={(e) => e.stopPropagation()}>
                          <td colSpan={6} className="expanded-block-performers-cell">
                            <div className="expanded-block-performers">
                              {isEditable && onAddSpecialist && (
                                <button
                                  type="button"
                                  className="add-performer-btn"
                                  onClick={() => onAddSpecialist(role.name, qualitySectionName)}
                                  disabled={!isEditable || isCalculating || isLoading}
                                >
                                  <span className="add-performer-btn-icon">+</span>
                                  Добавить исполнителя
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#666", fontSize: "16px" }}>
            Загрузка данных...
          </div>
        )}
      </div>

      <div className="quality-table-footer">
        <div className="quality-footer-main">
          <div className="quality-footer-label">ВСЕ ЗАТРАТЫ</div>
          <div className="quality-footer-sum main">
            ₸ {formatCurrency(qualityData.total_costs)}
          </div>
        </div>
        <div className="quality-footer-right">
          <div className="quality-footer-block">
            <div className="quality-footer-label">Прямые затраты</div>
            <div className="quality-footer-sum">
              ₸ {formatCurrency(qualityData.direct_costs)}
            </div>
          </div>
          <div className="quality-footer-block">
            <div className="quality-footer-label">Бонусы</div>
            <div className="quality-footer-sum">
              ₸ {formatCurrency(qualityData.bonus_fund)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualityTab;
